// Native (Capacitor) scheduling of task alarm notifications.
// Extracted so both the caregiver agenda and the global scheduler can reuse it.

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

export const TASK_ALARM_CHANNEL_ID = 'task-alarms';

export interface NativeAlarmConfig {
  alarm_enabled?: boolean | null;
  alarm_repeat_minutes?: number | null;
  alarm_max_repeats?: number | null;
}

export interface NativeAlarmTask {
  id: string;
  scheduled_for: string;
  isCompleted: boolean;
  name: string;
  patientId?: string | null;
}

export interface ScheduleOptions {
  body: string;
  /** When set, only alarms of this shift are replaced (keeps ids deterministic anyway). */
  logLabel?: string;
}

function hashId(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function taskAlarmNotificationId(instanceId: string, repeatIndex: number): number {
  return (hashId(instanceId) % 100000) * 100 + repeatIndex;
}

/** Creates the dedicated high-importance Android channel (no-op elsewhere). */
export async function ensureTaskAlarmChannel() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
  try {
    await LocalNotifications.createChannel({
      id: TASK_ALARM_CHANNEL_ID,
      name: 'Alarmes de tarefas',
      description: 'Alertas sonoros no horário das tarefas do plantão',
      importance: 5,
      visibility: 1,
      sound: 'default',
      vibration: true,
      lights: true,
    });
  } catch (e) {
    console.log('[task-alarms] channel creation skipped', e);
  }
}

export async function cancelTaskAlarmNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications.filter((n) => (n.extra as any)?.careosTaskAlarm);
    if (ours.length) {
      await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
    }
  } catch (e) {
    console.warn('[task-alarms] cancel failed', e);
  }
}

/**
 * Replaces all pending CareOS task alarms with alarms for the given pending tasks.
 * Safe to call repeatedly (idempotent).
 */
export async function scheduleTaskAlarmNotifications(
  tasks: NativeAlarmTask[],
  config: NativeAlarmConfig | null | undefined,
  options: ScheduleOptions,
): Promise<number> {
  if (!Capacitor.isNativePlatform()) return 0;

  try {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== 'granted') return 0;
    }

    await ensureTaskAlarmChannel();
    await cancelTaskAlarmNotifications();

    if (!config?.alarm_enabled) return 0;

    const repeatMinutes = Math.max(1, config.alarm_repeat_minutes ?? 5);
    const maxRepeats = Math.max(0, config.alarm_max_repeats ?? 3);
    const now = Date.now();

    const notifications = tasks
      .filter((task) => !task.isCompleted)
      .flatMap((task) => {
        const scheduled = new Date(task.scheduled_for).getTime();
        if (Number.isNaN(scheduled)) return [];
        return Array.from({ length: maxRepeats + 1 }, (_, i) => ({
          task,
          at: scheduled + i * repeatMinutes * 60_000,
          i,
        }));
      })
      .filter((entry) => entry.at > now + 5_000)
      .sort((a, b) => a.at - b.at)
      .slice(0, 60)
      .map((entry) => ({
        id: taskAlarmNotificationId(entry.task.id, entry.i),
        title: entry.task.name,
        body: options.body,
        channelId: TASK_ALARM_CHANNEL_ID,
        schedule: { at: new Date(entry.at), allowWhileIdle: true },
        extra: {
          careosTaskAlarm: true,
          instanceId: entry.task.id,
          patientId: entry.task.patientId ?? null,
        },
      }));

    if (notifications.length) {
      await LocalNotifications.schedule({ notifications });
    }
    console.log(`[task-alarms] scheduled ${notifications.length} alarms`, options.logLabel ?? '');
    return notifications.length;
  } catch (e) {
    console.warn('[task-alarms] native scheduling failed', e);
    return 0;
  }
}
